import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import MfaCodeStep from '../../components/MfaCodeStep';
import MfaEnrol from '../../components/MfaEnrol';
import { BrandMark, useBrand } from '../../brand/BrandProvider';

const MobileLogin = () => {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const notice = useLocation().state?.notice;
  const { login, completeLogin, loading } = useMobileAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const signedIn = await login(formData.email, formData.password);
      if (signedIn?.mfaRequired || signedIn?.enrolmentRequired) {
        setMfaStep({ kind: signedIn.mfaRequired ? 'code' : 'enrol', token: signedIn.mfaToken });
        return;
      }
      navigate('/mobile/dashboard');
    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BrandMark size="w-16 h-16" rounded="rounded-2xl" letterClass="text-2xl" />
          </div>
          <h1 className="font-display text-2xl font-bold text-ink mb-2">
            {brand.product_name}
          </h1>
          <p className="text-ink-muted">
            Mobile access for staff and management
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-surface rounded-2xl shadow-lg p-6">
          {mfaStep ? (mfaStep.kind === 'code' ? (
            <MfaCodeStep mfaToken={mfaStep.token}
              onVerified={async (data) => { await completeLogin(data); navigate('/mobile/dashboard'); }}
              onBack={() => setMfaStep(null)}
              onExpired={(message) => { setMfaStep(null); setError(message); }} />
          ) : (
            <div className="space-y-4" data-testid="mobile-forced-enrolment">
              <h2 className="text-lg font-semibold text-ink">Set up two-factor sign-in to continue</h2>
              <MfaEnrol authHeader={mfaStep.token}
                onDone={async (session) => {
                  if (!session) { setMfaStep(null); return; }
                  await completeLogin(session);
                  navigate('/mobile/dashboard');
                }} />
              <button type="button" onClick={() => setMfaStep(null)} data-testid="mobile-enrol-back-btn"
                className="min-h-[44px] px-2 text-sm text-primary-600 hover:underline">Back to sign in</button>
            </div>
          )) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {notice && !error && (
              <p role="status" className="p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700"
                data-testid="mobile-auth-notice">{notice}</p>
            )}
            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-danger/5 border border-danger/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ink mb-1">
                Email or username
              </label>
              <input
                type="text"
                id="email"
                name="email"
                autoCapitalize="none"
                spellCheck={false}
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-surface-border rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                placeholder="your@email.com or username"
                autoComplete="username"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-12 border border-surface-border rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-subtle hover:text-ink-muted"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary-600 border-surface-border rounded focus:ring-primary-300"
                />
                <span className="ml-2 text-sm text-ink-muted">Remember me</span>
              </label>
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="w-full flex items-center justify-center space-x-2 bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 focus:ring-2 focus:ring-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
          )}

          {/* Role Information */}
          <div className="mt-6 pt-6 border-t border-surface-border">
            <div className="text-center">
              <p className="text-sm text-ink-muted mb-3">
                Access levels available:
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-primary-50 text-primary-700 px-3 py-2 rounded-lg">
                  <strong>Admin:</strong> Full access
                </div>
                <div className="bg-primary-50 text-primary-700 px-3 py-2 rounded-lg">
                  <strong>Manager:</strong> Department access
                </div>
                <div className="bg-warn/10 text-accent-800 px-3 py-2 rounded-lg">
                  <strong>Team Lead:</strong> Team access
                </div>
                <div className="bg-surface-muted text-ink px-3 py-2 rounded-lg">
                  <strong>Staff:</strong> Basic access
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-ink-muted">
            © {new Date().getFullYear()} {brand.product_name}
          </p>
          <div className="mt-2 flex items-center justify-center space-x-4 text-xs text-ink-subtle">
            <a href="/privacy" className="hover:text-ink-muted">Privacy</a>
            <a href="/terms" className="hover:text-ink-muted">Terms</a>
            <a href="/support" className="hover:text-ink-muted">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLogin;
